import React, { useEffect, useState } from "react";
import {
  Modal,
  Input,
  Form,
  notification,
  Spin,
  Typography,
  Flex,
  Steps,
  Descriptions,
  Button,
  Table,
  Card,
} from "antd";
import {
  CheckOutlined,
  DownloadOutlined,
  EditOutlined,
  ExportOutlined,
  CloseOutlined,
  MailOutlined,
} from "@ant-design/icons";

import { cfbhAxios } from "utilities/axios";
import UploadDocumentsModal from "./UploadDocumentsModal";
import { useDispatch } from "react-redux";
import { fetchApplications } from "utilities/redux/slices/applicationsSlice";
import { useSelector } from "react-redux";
import useRoleCheck from "utilities/hooks/roleCheck";

const { Title, Paragraph } = Typography;

const ApplicationDetails = ({ id }) => {
  const [form] = Form.useForm();
  const { hasPermission } = useRoleCheck();
  const dispatch = useDispatch();

  const currentPage = useSelector((state) => state.applications.page);
  const currentSearch = useSelector((state) => state.applications.search || "");

  const [appInfo, setAppInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [stepError, setStepError] = useState(false);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [commentAction, setCommentAction] = useState(null);

  const loadApplicationDetails = () => {
    setLoading(true);

    cfbhAxios
      .get(`/applications/${id}`)
      .then((response) => {
        setAppInfo(response.data);
      })
      .catch((error) => {
        notification.error({
          message: "Error",
          description: `Failed to fetch application details: ${error.message}`,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadApplicationDetails();
  }, [id]);

  useEffect(() => {
    if (!appInfo) return;

    switch (appInfo?.application_status) {
      case "Submitted":
        setStep(0);
        setStepError(false);
        break;
      case "Approved":
        setStep(1);
        setStepError(false);
        break;
      case "Rejected":
        setStep(1);
        setStepError(true);
        break;
      case "Consent Granted":
        setStep(2);
        setStepError(false);
        break;
      case "Consent Denied":
        setStep(2);
        setStepError(true);
        break;
      case "Documents Uploaded":
        setStep(3);
        setStepError(false);
        break;
      case "Completed":
        setStep(4);
        setStepError(false);
        break;
      default:
        setStepError("Unknown application status");
    }
  }, [appInfo]);

  const steps = [
    {
      title: "Submitted",
    },
    {
      title: "Approved",
    },
    {
      title: "Client Consent Granted",
    },
    {
      title: "Documents Uploaded",
    },
    {
      title: "Completed",
    },
  ];

  const columns = [
    {
      key: 1,
      title: "Date",
      dataIndex: "nomination_date",
      render: (date) => {
        const d = new Date(date);
        return `${(d.getMonth() + 1).toString().padStart(2, "0")}/${d
          .getDate()
          .toString()
          .padStart(2, "0")}/${d.getFullYear()}`;
      },
    },
    {
      key: 2,
      title: "HP",
      dataIndex: "HP_Full_Name",
    },
    {
      key: 3,
      title: "Client",
      dataIndex: "client_name",
    },
    {
      key: 4,
      title: "Status",
      dataIndex: "application_status",
    },
  ];

  const handleDownload = (type) => {
    cfbhAxios
      .get(`/applications/${id}/${type}`, { responseType: "blob" })
      .then((res) => {
        const blob = new Blob([res.data], {
          type: res.headers["content-type"] || "application/octet-stream",
        });

        let filename = "";
        const disposition = res.headers["content-disposition"];
        if (disposition) {
          const match = disposition.match(/filename="?([^"]+)"?/);
          if (match && match[1]) {
            filename = match[1];
          }
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        notification.error({
          message: "Download Failed",
          description: `Failed to download ${type}: ${error.message}`,
        });
      });
  };

  // Helper to open comment modal for a given action
  const openCommentModal = (action) => {
    setCommentAction(action);
    form.resetFields();
    setCommentModalOpen(true);
  };

  // Helper to submit decision with comment
  const submitDecisionWithComment = async () => {
    if (!appInfo?.id || !commentAction) return;

    const values = await form.validateFields();
    let url = "";
    let successMsg = "";
    let successDesc = "";
    let errorMsg = "";
    let errorDesc = "";
    switch (commentAction) {
      case "approve":
        url = `/controllers/${appInfo.id}/approve`;
        successMsg = "Application Approved";
        successDesc = "The application was approved successfully.";
        errorMsg = "Approval Failed";
        errorDesc = "Unable to approve the application.";
        break;
      case "reject":
        url = `/controllers/${appInfo.id}/reject`;
        successMsg = "Application Rejected";
        successDesc = "The application was rejected successfully.";
        errorMsg = "Rejection Failed";
        errorDesc = "Unable to reject the application.";
        break;
      case "consentGranted":
        url = `/controllers/${appInfo.id}/client-consent-granted`;
        successMsg = "Consent Granted";
        successDesc = "Client consent was granted successfully.";
        errorMsg = "Consent Grant Failed";
        errorDesc = "Unable to grant client consent.";
        break;
      case "consentRejected":
        url = `/controllers/${appInfo.id}/client-consent-rejected`;
        successMsg = "Consent Denied";
        successDesc = "Client consent was denied successfully.";
        errorMsg = "Consent Denial Failed";
        errorDesc = "Unable to deny client consent.";
        break;
      case "complete":
        url = `/controllers/${appInfo.id}/complete`;
        successMsg = "Application Completed";
        successDesc = "The application was marked as completed successfully.";
        errorMsg = "Completion Failed";
        errorDesc = "Unable to mark the application as completed.";
        break;
      default:
        return;
    }
    cfbhAxios
      .post(url, { comments: values.comments })
      .then(() => {
        notification.success({
          message: successMsg,
          description: successDesc,
        });
        setCommentModalOpen(false);
        setCommentAction(null);
        loadApplicationDetails();
        dispatch(
          fetchApplications({ search: currentSearch, page: currentPage })
        );
      })
      .catch((error) => {
        notification.error({
          message: errorMsg,
          description: `${errorDesc} ${error.message}`,
        });
      });
  };

  if (loading) {
    return (
      <Card
        style={{
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Spin size="large" />
      </Card>
    );
  }

  // Dynamically set step titles based on stepError
  const dynamicSteps = steps.map((stepItem, idx) => {
    if (!stepError) return stepItem;
    // Example: append " (Error)" to the current step if there's an error
    if (idx === 1) {
      return { ...stepItem, title: "Rejected" };
    } else if (idx === 2) {
      return { ...stepItem, title: "Client Consent Denied" };
    }
    return stepItem;
  });

  // Helper to format date to MM/DD/YYYY
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (isNaN(date)) return dateStr;
    return `${(date.getMonth() + 1).toString().padStart(2, "0")}/${date
      .getDate()
      .toString()
      .padStart(2, "0")}/${date.getFullYear()}`;
  };

  return (
    <Card
      title={
        <Flex align="center" justify="space-between" style={{ width: "100%" }}>
          <Title level={3} style={{ marginTop: 16, marginBottom: 8 }}>
            {appInfo?.client_name}
          </Title>
          <Flex justify="center" style={{ gap: "16px" }}>
            {hasPermission(["Audiology.Admin"]) && (
              <Button
                icon={<MailOutlined />}
                styles={{
                  root: {
                    height: 40,
                    fontSize: 20,
                    boxShadow: "none",
                  },
                }}
                type="primary"
                variant="solid"
                onClick={() => {
                  cfbhAxios
                    .post(`/controllers/${id}/resend-email`)
                    .then(() => {
                      notification.success({
                        message: "Email Sent",
                        description: "Status update email resent successfully.",
                      });
                    })
                    .catch((error) => {
                      notification.error({
                        message: "Email Failed",
                        description: `Failed to resend status update email: ${error.message}`,
                      });
                    });
                }}
              >
                Resend Email
              </Button>
            )}
            {!stepError &&
              (step === 0 || step === 1 || step === 3 ? (
                <>
                  {(hasPermission(["Audiology.Admin"]) || step === 1) && (
                    <Button
                      styles={{
                        root: {
                          backgroundColor: "#ACE8B1",
                          height: 40,
                          fontSize: 20,
                          flex: 1,
                        },
                      }}
                      variant="solid"
                      icon={<CheckOutlined />}
                      onClick={() =>
                        openCommentModal(
                          step === 0
                            ? "approve"
                            : step === 1
                            ? "consentGranted"
                            : "complete"
                        )
                      }
                    >
                      {step === 0
                        ? "Approve"
                        : step === 1
                        ? "Client Consent Granted"
                        : "Mark as Completed"}
                    </Button>
                  )}
                  {((hasPermission(["Audiology.Admin"]) && step === 0) ||
                    step === 1) && (
                    <Button
                      type="primary"
                      danger
                      icon={<CloseOutlined />}
                      onClick={() =>
                        openCommentModal(
                          step === 0 ? "reject" : "consentRejected"
                        )
                      }
                      style={{
                        height: 40,
                        minWidth: 140,
                        fontSize: 20,
                        flex: 1,
                      }}
                    >
                      {step === 0 ? "Reject" : "Client Consent Denied"}
                    </Button>
                  )}
                </>
              ) : step === 2 ? (
                <>
                  <Button
                    variant="solid"
                    icon={<EditOutlined />}
                    onClick={() => setEditModalOpen(true)}
                    styles={{
                      root: {
                        backgroundColor: "#ACE8B1",
                        height: 40,
                        fontSize: 20,
                        flex: 1,
                      },
                    }}
                  >
                    Update Information & Documents
                  </Button>
                  <Button
                    type="primary"
                    icon={<ExportOutlined />}
                    onClick={() => {
                      cfbhAxios
                        .get(`/documents/download`, {
                          responseType: "blob",
                        })
                        .then((response) => {
                          const url = window.URL.createObjectURL(
                            new Blob([response.data])
                          );
                          const link = document.createElement("a");
                          link.href = url;
                          link.setAttribute(
                            "download",
                            `application_documents.zip`
                          );
                          document.body.appendChild(link);
                          link.click();
                          link.parentNode.removeChild(link);
                          window.URL.revokeObjectURL(url);
                        })
                        .catch((error) => {
                          notification.error({
                            message: "Export Failed",
                            description: `Failed to download documents: ${error.message}`,
                          });
                        });
                    }}
                    style={{ height: 40, minWidth: 140, fontSize: 20, flex: 1 }}
                  >
                    View Documents
                  </Button>
                </>
              ) : null)}
          </Flex>
        </Flex>
      }
      styles={{
        body: {
          flex: 1,
          overflow: "auto",
          paddingBottom: 8,
        },
        root: {
          height: "100%",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Flex vertical style={{ height: "100%" }}>
        <Steps
          titlePlacement="vertical"
          current={step}
          status={stepError ? "error" : "finish"}
          style={{ width: "100%" }}
          responsive={true}
          items={dynamicSteps.map((item) => ({
            key: item.title,
            title: item.title,
          }))}
          onChange={(value) => {
            if (hasPermission(["Audiology.Admin"]) && value < step) {
              cfbhAxios
                .post(`/controllers/${id}`, { action: value })
                .then(() => {
                  loadApplicationDetails();
                  dispatch(
                    fetchApplications({
                      search: currentSearch,
                      page: currentPage,
                    })
                  );
                })
                .catch((error) => {
                  notification.error({
                    message: "Error",
                    description: `Failed to update application status: ${error.message}`,
                  });
                });
            }
          }}
        />
        <Descriptions
          bordered
          column={3}
          style={{ margin: "24px 0 12px 0" }}
          size="small"
          layout=""
        >
          <Descriptions.Item span={1} label="Submission Date">
            {formatDate(appInfo?.nomination_date)}
          </Descriptions.Item>
          <Descriptions.Item span={1} label="Region">
            {appInfo?.Region_Name}
          </Descriptions.Item>
          <Descriptions.Item span={1} label="Division">
            {appInfo?.Division_Name}
          </Descriptions.Item>
          <Descriptions.Item span={1} label="HP">
            {appInfo?.HP_Full_Name}
          </Descriptions.Item>
          <Descriptions.Item
            span={2}
            label="Clinic"
          >{`${appInfo?.Clinic_Name} (${appInfo?.clinic_no})`}</Descriptions.Item>
          <Descriptions.Item span={3} label="Reason">
            <Paragraph
              ellipsis={{ rows: 4, expandable: true, symbol: "more" }}
              style={{ margin: 0 }}
            >
              {appInfo?.nomination_reason}
            </Paragraph>
          </Descriptions.Item>
          <Descriptions.Item span={1} label="Aid Make">
            {appInfo?.hearing_aid_make}
          </Descriptions.Item>
          <Descriptions.Item span={1} label="Aid Model">
            {appInfo?.hearing_aid_model}
          </Descriptions.Item>
          <Descriptions.Item span={1} label="Aid Count">
            {appInfo?.hearing_aid_count === 2 ? "Binaural" : "Monaural"}
          </Descriptions.Item>
          <Descriptions.Item span={1} label="Invoice Cost">
            ${appInfo?.invoice_total}
          </Descriptions.Item>
          {step >= 3 && (
            <>
              <Descriptions.Item span={1} label="Dispense Date">
                {formatDate(appInfo?.dispense_date)}
              </Descriptions.Item>
              <Descriptions.Item span={2} label="Serial Number(s)">
                {appInfo?.hearing_aid_count === 2
                  ? `${appInfo?.serial_no}, ${appInfo?.serial_no_2}`
                  : appInfo?.serial_no}
              </Descriptions.Item>
            </>
          )}
        </Descriptions>
        <Flex justify="end" style={{ width: "100%" }}>
          {step >= 3 && (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload("documents")}
            >
              Documents
            </Button>
          )}
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => handleDownload("audiogram")}
            style={{ marginLeft: step >= 3 ? 8 : 0 }}
          >
            Audiogram
          </Button>
        </Flex>
        {hasPermission(["Admin.Manage", "Audiology"]) && (
          <>
            <Title level={5} style={{ marginTop: 16 }}>
              Previous Submissions for this Clinic
            </Title>
            <Table
              pagination={{
                pageSize: 3,
              }}
              bordered
              dataSource={appInfo?.previous_submissions}
              columns={columns}
              style={{ marginTop: 0 }}
            />
          </>
        )}
        <Modal
          title="Add Comments"
          open={commentModalOpen}
          onOk={submitDecisionWithComment}
          onCancel={() => {
            setCommentModalOpen(false);
            setCommentAction(null);
            form.resetFields();
          }}
          okText="Submit"
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="comments"
              label="Comments/Reason for Decision"
              rules={[{ required: false, max: 500 }]}
            >
              <Input.TextArea
                placeholder="Enter your comments/reason for this decision"
                rows={4}
              />
            </Form.Item>
          </Form>
        </Modal>
        <UploadDocumentsModal
          visible={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            loadApplicationDetails();
          }}
          applicationId={appInfo?.id}
          appInfo={appInfo}
        />
      </Flex>
    </Card>
  );
};

export default ApplicationDetails;
